import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ClientCard,
  ClientStatus,
  UnifiedOrderPage,
  OrderType,
} from "@mnp/shared";
import { api } from "./api";

// API serializes BigInt → string and Decimal → string (see lib/bigintJson on
// the API side). Mirror that here so consumers never get a runtime `bigint`.

export interface ClientPage {
  rows: ClientCard[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ClientNote {
  id: string;
  userChatId: string;
  authorChatId: string;
  text: string;
  createdAt: string;
}

export interface ClientDocument {
  id: string;
  userChatId: string;
  label: string;
  fileId: string;
  sizeBytes: number;
  verified: boolean;
  uploadedAt: string;
}

export interface ClientLink {
  id: string;
  fromChatId: string;
  toChatId: string;
  relation: string;
  createdAt: string;
}

export interface ClientPaymentRow {
  id: string;
  orderId: string | null;
  shopOrderId: string | null;
  chatId: string;
  fileId: string;
  fileType: string;
  status: string;
  aiPaidAmount: string | null;
  aiTargetAmount: string | null;
  aiMessage: string | null;
  createdAt: string;
}

export interface ClientsQuery {
  search?: string;
  status?: ClientStatus;
  page?: number;
  pageSize?: number;
}

function buildClientsQuery(q: ClientsQuery): string {
  const params = new URLSearchParams();
  if (q.search && q.search.trim()) params.set("search", q.search.trim());
  if (q.status) params.set("status", q.status);
  if (q.page) params.set("page", String(q.page));
  if (q.pageSize) params.set("pageSize", String(q.pageSize));
  const s = params.toString();
  return s ? `?${s}` : "";
}

export const CLIENTS_QUERY_KEY = "admin-clients";
export const CLIENT_QUERY_KEY = "admin-client";
export const CLIENT_ORDERS_QUERY_KEY = "admin-client-orders";
export const CLIENT_PAYMENTS_QUERY_KEY = "admin-client-payments";
export const CLIENT_NOTES_QUERY_KEY = "admin-client-notes";
export const CLIENT_DOCS_QUERY_KEY = "admin-client-docs";
export const CLIENT_LINKS_QUERY_KEY = "admin-client-links";

export function useClients(query: ClientsQuery = {}) {
  return useQuery({
    queryKey: [CLIENTS_QUERY_KEY, query],
    queryFn: () => api<ClientPage>(`/admin/clients${buildClientsQuery(query)}`),
  });
}

export function useClient(id: string | null) {
  return useQuery({
    queryKey: [CLIENT_QUERY_KEY, id],
    queryFn: () => api<ClientCard>(`/admin/clients/${id}`),
    enabled: !!id,
  });
}

export function useClientOrders(id: string | null, type: OrderType = "all") {
  return useQuery({
    queryKey: [CLIENT_ORDERS_QUERY_KEY, id, type],
    queryFn: () =>
      api<UnifiedOrderPage>(`/admin/clients/${id}/orders?type=${type}`),
    enabled: !!id,
  });
}

export function useClientPayments(id: string | null) {
  return useQuery({
    queryKey: [CLIENT_PAYMENTS_QUERY_KEY, id],
    queryFn: () => api<ClientPaymentRow[]>(`/admin/clients/${id}/payments`),
    enabled: !!id,
  });
}

export function useClientNotes(id: string | null) {
  return useQuery({
    queryKey: [CLIENT_NOTES_QUERY_KEY, id],
    queryFn: () => api<ClientNote[]>(`/admin/clients/${id}/notes`),
    enabled: !!id,
  });
}

export function useAddClientNote(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (text: string) =>
      api<ClientNote>(`/admin/clients/${id}/notes`, {
        method: "POST",
        body: JSON.stringify({ text }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CLIENT_NOTES_QUERY_KEY, id] });
    },
  });
}

export function useClientDocs(id: string | null) {
  return useQuery({
    queryKey: [CLIENT_DOCS_QUERY_KEY, id],
    queryFn: () => api<ClientDocument[]>(`/admin/clients/${id}/docs`),
    enabled: !!id,
  });
}

export function useUploadClientDoc(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, label }: { file: File; label?: string }) => {
      const form = new FormData();
      form.append("file", file);
      if (label) form.append("label", label);
      return api<ClientDocument>(`/admin/clients/${id}/docs`, {
        method: "POST",
        body: form,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CLIENT_DOCS_QUERY_KEY, id] });
    },
  });
}

export function useClientLinks(id: string | null) {
  return useQuery({
    queryKey: [CLIENT_LINKS_QUERY_KEY, id],
    queryFn: () => api<ClientLink[]>(`/admin/clients/${id}/links`),
    enabled: !!id,
  });
}

export function useAddClientLink(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { toChatId: string; relation: string }) =>
      api(`/admin/clients/${id}/links`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CLIENT_LINKS_QUERY_KEY, id] });
    },
  });
}

export function useRemoveClientLink(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) =>
      api(`/admin/clients/${id}/links/${linkId}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CLIENT_LINKS_QUERY_KEY, id] });
    },
  });
}
